import PageLayout from "@/components/PageLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Database, Globe, FileText, CheckCircle, Users, Zap, Image, Truck, Package } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import RelatedFeaturesSection from "@/components/RelatedFeaturesSection";
import FAQSection from "@/components/FAQSection";

const PIM = () => {
  useSEO({
    title: 'Product Information Management (PIM) System | Ommni',
    description: 'Centralize product data, enrich content, and publish to all channels with Ommni PIM. Single source of truth for product information management across your organization.',
    keywords: 'PIM system, product information management, product data management, multi-channel publishing, product content management',
    canonical: 'https://ommni.io/features/pim',
  });

  const features = [
    {
      icon: Database,
      title: "Centralized Product Data",
      description: "Single source of truth for all product information across your entire organization.",
    },
    {
      icon: Globe,
      title: "Multi-Channel Publishing",
      description: "Distribute accurate product data to all sales channels, marketplaces, and platforms instantly.",
    },
    {
      icon: FileText,
      title: "Data Enrichment",
      description: "Enhance product information with descriptions, specifications, attributes, and metadata.",
    },
    {
      icon: CheckCircle,
      title: "Data Quality Control",
      description: "Validate product information with automated rules and completeness checks.",
    },
    {
      icon: Users,
      title: "Collaborative Workflows",
      description: "Enable teams to collaborate on product data with role-based access and approval workflows.",
    },
    {
      icon: Zap,
      title: "Bulk Operations",
      description: "Update thousands of products simultaneously with powerful bulk editing tools.",
    },
  ];

  const benefits = [
    {
      title: "Reduce Time to Market",
      description: "Launch products faster with streamlined data management and automated publishing.",
    },
    {
      title: "Improve Data Accuracy",
      description: "Eliminate inconsistencies and errors with centralized, validated product information.",
    },
    {
      title: "Scale with Ease",
      description: "Manage growing product catalogs without increasing operational complexity.",
    },
    {
      title: "Enhance Customer Experience",
      description: "Provide consistent, accurate product information across all customer touchpoints.",
    },
  ];

  const faqs = [
    {
      question: "What is a PIM system and why do I need one?",
      answer: "A Product Information Management (PIM) system is a centralized platform that stores, manages, and enriches all product information. It's essential for businesses managing large catalogs, selling across multiple channels, or needing to maintain consistent product data quality."
    },
    {
      question: "How does Ommni PIM integrate with my existing systems?",
      answer: "Ommni PIM integrates seamlessly with over 100+ platforms including ecommerce systems, ERPs, marketplaces, and sales channels through pre-built connectors and APIs. Our integration team ensures smooth data flow between all your systems."
    },
    {
      question: "Can I manage product variants and complex relationships?",
      answer: "Yes, Ommni PIM fully supports product variants, bundles, kits, and complex hierarchical relationships. You can manage parent-child relationships, linked products, and cross-sells all within the platform."
    },
    {
      question: "How long does it take to implement Ommni PIM?",
      answer: "Implementation typically takes 4-8 weeks depending on catalog size and integration complexity. Our guided onboarding process includes data migration, system setup, team training, and go-live support."
    },
    {
      question: "What file formats does Ommni PIM support for import/export?",
      answer: "Ommni PIM supports CSV, Excel, JSON, XML, and custom formats. You can also use our API for real-time data synchronization with your systems."
    },
    {
      question: "How does Ommni PIM ensure data quality?",
      answer: "Ommni PIM includes built-in validation rules, completeness checks, duplicate detection, and automated data quality scoring. You can set up custom validation rules to match your business requirements and ensure all product data meets your standards before publishing."
    }
  ];

  return (
    <PageLayout>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Ommni PIM - Product Information Management",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "Manage, enrich, and distribute product information across all your sales channels from a single, powerful platform.",
          "offers": {
            "@type": "Offer",
            "price": "500",
            "priceCurrency": "USD",
            "description": "Starting at $500/month"
          },
          "provider": {
            "@type": "Organization",
            "name": "Ommni",
            "url": "https://ommni.io"
          },
          "featureList": [
            "Centralized Product Data",
            "Multi-Channel Publishing",
            "Data Enrichment",
            "Data Quality Control",
            "Collaborative Workflows",
            "Bulk Operations"
          ]
        })}
      </script>
      <div className="px-4 pb-24">
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Database className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Product Information Management
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Manage, enrich, and distribute product information across all your sales channels from a single, powerful platform.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Start Free Trial</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Schedule Demo</Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Powerful PIM Capabilities</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="p-8 gradient-card">
                  <div className="mb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Business Benefits</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Related Features */}
          <RelatedFeaturesSection
            features={[
              {
                icon: Image,
                title: "Digital Asset Management",
                description: "Store, organize, and distribute product images and media alongside your product data.",
                link: "/features/dam",
              },
              {
                icon: Truck,
                title: "Order Management",
                description: "Route and fulfill orders intelligently with real-time inventory visibility.",
                link: "/features/dom",
              },
              {
                icon: Package,
                title: "Inventory Management",
                description: "Track stock levels in real-time across all locations and sales channels.",
                link: "/features/inventory",
              },
            ]}
          />

          {/* FAQ Section */}
          <FAQSection 
            faqs={faqs}
            title="Frequently Asked Questions"
            description="Common questions about Ommni's Product Information Management system."
          />

          {/* CTA Section */}
          <Card className="p-12 bg-primary/5 border-2 border-primary/20 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Transform Your Product Data?
            </h2>
            <p className="text-foreground/80 max-w-2xl mx-auto mb-8 text-lg">
              Join leading retailers and brands who trust Ommni PIM to manage their product information at scale.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Get Started</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default PIM;
